/**
 * A card's right-click menu (ADR 0194, decision 3), as the owner's prototype
 * has it (its `cmItems`): everything the block's popup offers for that card,
 * plus Open and Copy summary, by its column. And the selection's menu (its
 * `bulkItems`): with several cards selected, a right-click on one of them
 * has what the selection bar has (decision 5).
 *
 * - **Ready:** Log… (it opens the Log form; idea #875), "Log to ticket T… (open)" when it continues an open
 *   ticket, Add to existing ticket…, Open; Change client…, Duration…, Move
 *   an activity…, Merge with ▸, Split…; Dismiss ▸; Copy summary.
 * - **Needs you:** Assign client… (a block with no client), Open, then as
 *   Ready from Change client… on, and Vendor… where there's someone to
 *   remember.
 * - **Logged - Open:** Open, Close ticket…, Open ticket in Autotask, Copy
 *   ticket number, "Log a Ready block here ▸", Copy summary.
 * - **Closed** (and a logged block whose ticket isn't open): Open, Open
 *   ticket in Autotask, Copy ticket number, "Reopen and log a Ready block ▸"
 *   when it's closed (P9; ADR 0197), Copy summary.
 *
 * Each "…" item opens the block's popup on its pane or form (`openBlock`'s
 * options). Dismiss ▸'s reasons dismiss at once, and Merge with ▸'s blocks
 * merge at once, as the popup's own menus do. Which panes a block has is the
 * popup's rule (`popupPanes`), so the two never disagree. A ticket's card's
 * menu is its first block's, with the card's times and total. Its items'
 * hints are silent, and none has its own `ariaLabel` (#796).
 */
import type { ReactNode } from "react";

import type { DismissReason } from "../../api/types";
import {
  ClockIcon,
  CopyIcon,
  CrossIcon,
  EmailIcon,
  ExpandIcon,
  ExternalIcon,
  EyeOffIcon,
  PlusIcon,
} from "../../components/ui/Icons";
import { MENU_SEPARATOR, type MenuEntry, type MenuHeader } from "../../components/ui/useMenu";
import { formatClockRange, formatShortDay, todayIn } from "../../lib/dates";
import { formatDuration } from "../../lib/durations";
import type { OpenOptions } from "./BlockDetails";
import {
  type BlockView,
  DISMISS_REASONS,
  type PopupPane,
  blockSummary,
  mergeable,
  openTicketToAdd,
  popupPanes,
  readyForTicket,
} from "./model";
import type { BulkAction, BulkActionId, PickableCard } from "./selection";
import { OPEN_COLUMN, type TicketView, canClose, ticketName } from "./tickets";

/** What a card's menu does: the day view's. */
export interface CardMenuActions {
  timeZone: string;
  /** The day's blocks still to log: what a block merges with. */
  targets: readonly BlockView[];
  /** Every block on the board: the Ready ones a logged block's ticket can take. */
  blocks: readonly BlockView[];
  /** Opens a block's popup, on a pane or a form (`openBlock`). */
  open: (blockId: string, options?: OpenOptions) => void;
  /** Dismisses a block at once, for this reason. */
  dismiss: (block: BlockView, reason: DismissReason) => void;
  /** Merges two blocks at once (ADR 0118). */
  merge: (block: BlockView, other: BlockView) => void;
  /** Copies, or shows it to copy by hand. */
  copy: (text: string) => void;
  /** Opens a logged block's ticket in Autotask; null where there's no link to give (privacy mode). */
  openTicket: ((block: BlockView) => void) | null;
}

/** A card's times: each block's, or the first to the last when there are more than three. */
function timesOf(card: PickableCard, timeZone: string): string {
  const blocks = card.blocks;
  const [first] = blocks;
  if (first === undefined) {
    return "";
  }
  if (blocks.length > 3) {
    const last = Math.max(...blocks.map((b) => Date.parse(b.endedAt)));
    return formatClockRange(first.startedAt, new Date(last).toISOString(), timeZone);
  }
  return blocks.map((b) => formatClockRange(b.startedAt, b.endedAt, timeZone)).join(", ");
}

/** A card's duration: its block's, or its ticket's total, with time entered in Autotask. */
function durationOf(card: PickableCard): string {
  if (card.card.kind === "ticket") {
    return card.card.ticket.duration;
  }
  return card.blocks[0]?.duration ?? formatDuration(0);
}

/** The menu's header: the card's client, its times and its duration. */
export function cardMenuHeader(card: PickableCard, timeZone: string): MenuHeader {
  return { title: card.title, line: `${timesOf(card, timeZone)} · ${durationOf(card)}` };
}

/** The menu's name, for a screen reader (its header isn't read): "Pinecrest Law, 9:00–9:40". */
export function cardMenuLabel(card: PickableCard, timeZone: string): string {
  return `${card.title}, ${timesOf(card, timeZone)}`;
}

/** What Copy summary copies: a block's (`blockSummary`), or a ticket's card's, with its times and total. */
export function cardSummary(card: PickableCard, timeZone: string): string {
  const [first] = card.blocks;
  if (first === undefined) {
    return card.title;
  }
  if (card.card.kind !== "ticket" || card.ticket === null) {
    return blockSummary(first, timeZone);
  }
  return [
    card.title,
    formatShortDay(todayIn(timeZone, new Date(first.startedAt))),
    timesOf(card, timeZone),
    durationOf(card),
    card.ticket.state === "closed" ? "Closed" : OPEN_COLUMN,
    `Ticket ${ticketName(card.ticket)}`,
  ].join(" · ");
}

function dismissItems(block: BlockView, actions: CardMenuActions): MenuEntry[] {
  return [
    ...DISMISS_REASONS.filter((r) => r.reason !== "other").map(
      (r): MenuEntry => ({
        label: r.label,
        warn: true,
        onSelect: () => {
          actions.dismiss(block, r.reason);
        },
      }),
    ),
    MENU_SEPARATOR,
    {
      label: "Other…",
      onSelect: () => {
        actions.open(block.id, { pane: "dismiss", reason: "other" });
      },
    },
  ];
}

function mergeItems(block: BlockView, actions: CardMenuActions): MenuEntry[] {
  return [
    ...mergeable(block, actions.targets).map(
      (other): MenuEntry => ({
        label: `${other.title} · ${formatClockRange(other.startedAt, other.endedAt, actions.timeZone)}`,
        hint: other.duration,
        onSelect: () => {
          actions.merge(block, other);
        },
      }),
    ),
    MENU_SEPARATOR,
    {
      label: "Choose in the popup…",
      onSelect: () => {
        actions.open(block.id, { pane: "merge" });
      },
    },
  ];
}

/** "Log a Ready block here ▸": each Ready block the ticket can take, its Log form on this ticket. */
function logHereItems(block: BlockView, ticket: TicketView, actions: CardMenuActions): MenuEntry[] {
  const ready = readyForTicket(block, actions.blocks);
  if (ready.length === 0) {
    return [{ label: "No Ready blocks for this client", disabled: true }];
  }
  return ready.map(
    (other): MenuEntry => ({
      label: formatClockRange(other.startedAt, other.endedAt, actions.timeZone),
      hint: other.duration,
      onSelect: () => {
        actions.open(other.id, {
          logStart: { blockId: other.id, ticketId: ticket.ticketId, number: ticket.number, closed: ticket.state === "closed" },
        });
      },
    }),
  );
}

/** A card's menu, by its column (the prototype's `cmItems`). */
export function cardMenuItems(card: PickableCard, actions: CardMenuActions): MenuEntry[] {
  const [block] = card.blocks;
  if (block === undefined) {
    return [];
  }
  const panes = popupPanes(block, actions.targets, actions.timeZone);
  const pane = (target: PopupPane, label: string, icon?: ReactNode): MenuEntry[] =>
    panes.includes(target)
      ? [
          {
            label,
            icon,
            onSelect: () => {
              actions.open(block.id, { pane: target });
            },
          },
        ]
      : [];
  const open: MenuEntry = {
    label: "Open",
    icon: <ExpandIcon />,
    hint: "Enter",
    onSelect: () => {
      actions.open(block.id);
    },
  };
  const copySummary: MenuEntry = {
    label: "Copy summary",
    icon: <CopyIcon />,
    onSelect: () => {
      actions.copy(cardSummary(card, actions.timeZone));
    },
  };

  if (block.canDismiss) {
    const first: MenuEntry[] = [];
    if (block.kind === "ready") {
      first.push({
        label: "Log…",
        onSelect: () => {
          actions.open(block.id, { focusForm: true });
        },
      });
      const continues = openTicketToAdd(block);
      if (continues !== null) {
        first.push({
          label: `Log to ticket ${ticketName(continues)} (open)`,
          onSelect: () => {
            actions.open(block.id, {
              logStart: { blockId: block.id, ticketId: continues.ticketId, number: continues.number, closed: false },
            });
          },
        });
      }
      first.push({
        label: "Add to existing ticket…",
        icon: <PlusIcon />,
        onSelect: () => {
          actions.open(block.id, { ticketListOpen: true });
        },
      });
    } else if (block.kind === "needs_client") {
      first.push({
        label: "Assign client…",
        onSelect: () => {
          actions.open(block.id, { focusForm: true });
        },
      });
    }
    return [
      ...first,
      open,
      MENU_SEPARATOR,
      ...pane("client", "Change client…"),
      ...pane("duration", "Duration…", <ClockIcon />),
      ...pane("move", "Move an activity…"),
      ...(panes.includes("merge") ? [{ label: "Merge with", items: () => mergeItems(block, actions) }] : []),
      ...pane("split", "Split…"),
      MENU_SEPARATOR,
      { label: "Dismiss", icon: <EyeOffIcon />, warn: true, items: () => dismissItems(block, actions) },
      ...pane("vendor", "Vendor…", <EmailIcon />),
      MENU_SEPARATOR,
      copySummary,
    ];
  }

  const ticket = block.kind === "logged" ? card.ticket : null;
  if (ticket === null) {
    // Being logged, didn't log, dismissed, or logged with no ticket read back.
    return [open, MENU_SEPARATOR, ...pane("client", "Change client…"), MENU_SEPARATOR, copySummary];
  }
  const name = ticketName(ticket);
  const openTicket = actions.openTicket;
  const closes = canClose(ticket.state);
  return [
    open,
    ...(closes
      ? [
          {
            label: "Close ticket…",
            onSelect: () => {
              actions.open(block.id, { closeTicket: true });
            },
          },
        ]
      : []),
    ...(openTicket === null
      ? []
      : [
          {
            label: `Open ticket ${name} in Autotask`,
            icon: <ExternalIcon />,
            onSelect: () => {
              openTicket(block);
            },
          },
        ]),
    {
      label: "Copy ticket number",
      icon: <CopyIcon />,
      onSelect: () => {
        actions.copy(name);
      },
    },
    MENU_SEPARATOR,
    ...(closes ? [{ label: "Log a Ready block here", items: () => logHereItems(block, ticket, actions) }, MENU_SEPARATOR] : []),
    // A closed ticket is reopened to log to it (P9; ADR 0197): the block's Log
    // form opens on it, and says so.
    ...(ticket.state === "closed"
      ? [{ label: "Reopen and log a Ready block", items: () => logHereItems(block, ticket, actions) }, MENU_SEPARATOR]
      : []),
    copySummary,
  ];
}

/** What the selection's menu and bar do: the day view's. */
export interface BulkMenuActions {
  dismiss: (reason: DismissReason) => void;
  merge: () => void;
  /** Mark as vendor… or Close tickets…: the bar's confirm. */
  ask: (id: Extract<BulkActionId, "vendor" | "close">) => void;
  clear: () => void;
}

/** Bulk Dismiss's reasons: one for them all, Other too, with no note. */
export function bulkDismissItems(actions: Pick<BulkMenuActions, "dismiss">): MenuEntry[] {
  return DISMISS_REASONS.map(
    (r): MenuEntry => ({
      label: r.label,
      warn: true,
      onSelect: () => {
        actions.dismiss(r.reason);
      },
    }),
  );
}

/**
 * The selection's menu (the prototype's `bulkItems`): the bar's actions, an
 * action that only some of the selection could take part in greyed with its
 * reason, then Clear selection.
 */
export function bulkMenuItems(available: readonly BulkAction[], actions: BulkMenuActions): MenuEntry[] {
  const items: MenuEntry[] = available.map((action): MenuEntry => {
    const off = action.ok ? {} : { disabled: true, reason: action.why ?? undefined };
    switch (action.id) {
      case "dismiss":
        return action.ok
          ? { label: "Dismiss", icon: <EyeOffIcon />, warn: true, items: () => bulkDismissItems(actions) }
          : { label: "Dismiss", icon: <EyeOffIcon />, ...off };
      case "merge":
        return { label: action.label, onSelect: actions.merge, ...off };
      case "vendor":
      case "close": {
        const id = action.id;
        return {
          label: action.label,
          onSelect: () => {
            actions.ask(id);
          },
          ...off,
        };
      }
    }
  });
  return [
    ...items,
    MENU_SEPARATOR,
    { label: "Clear selection", icon: <CrossIcon />, hint: "Esc", onSelect: actions.clear },
  ];
}
